import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Save, Sparkles, Globe, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { key: 'en', label: 'English' },
  { key: 'ar', label: 'العربية', dir: 'rtl' },
  { key: 'fr', label: 'Français' },
];

const FIELDS = [
  { key: 'generic_name', label: { en: 'Generic Name', ar: 'الاسم العلمي', fr: 'Nom générique' } },
  { key: 'category', label: { en: 'Category', ar: 'الفئة', fr: 'Catégorie' } },
  { key: 'description', label: { en: 'Description', ar: 'الوصف', fr: 'Description' } },
  { key: 'indications', label: { en: 'Indications', ar: 'دواعي الاستعمال', fr: 'Indications' } },
  { key: 'dosage', label: { en: 'Dosage', ar: 'الجرعة', fr: 'Posologie' } },
  { key: 'side_effects', label: { en: 'Side Effects', ar: 'الآثار الجانبية', fr: 'Effets secondaires' } },
  { key: 'contraindications', label: { en: 'Contraindications', ar: 'موانع الاستعمال', fr: 'Contre-indications' } },
  { key: 'storage', label: { en: 'Storage', ar: 'التخزين', fr: 'Conservation' } },
];

// Parse saved description text back into structured {en, ar, fr} object
function parseSavedDescription(text) {
  if (!text) return null;
  const langHeaders = { '[English]': 'en', '[العربية]': 'ar', '[Français]': 'fr' };
  const result = {};
  let currentLang = null;
  let currentLines = [];

  const flush = () => {
    if (currentLang && currentLines.length) {
      const data = {};
      for (const line of currentLines) {
        for (const field of FIELDS) {
          const label = field.label[currentLang] || field.label.en;
          if (line.startsWith(label + ': ')) {
            data[field.key] = line.substring(label.length + 2);
            break;
          }
        }
      }
      if (Object.keys(data).length > 0) result[currentLang] = data;
    }
  };

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const langKey = langHeaders[trimmed];
    if (langKey) {
      flush();
      currentLang = langKey;
      currentLines = [];
    } else if (currentLang) {
      currentLines.push(trimmed);
    }
  }
  flush();

  return Object.keys(result).length > 0 ? result : null;
}

const AIMedicineInfoDialog = ({ open, onOpenChange, itemName, itemId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [fromSaved, setFromSaved] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [error, setError] = useState(null);

  // On open: check for saved description first
  useEffect(() => {
    if (open && itemId) {
      setInfo(null);
      setError(null);
      setFromSaved(false);
      setActiveLang('en');
      setLoadingSaved(true);
      axios.get(`${API}/inventory/${itemId}/description`)
        .then(res => {
          const desc = res.data?.description;
          if (desc) {
            const parsed = parseSavedDescription(desc);
            if (parsed) {
              setInfo(parsed);
              setFromSaved(true);
              return;
            }
          }
          // No saved description — show fetch button
        })
        .catch(() => {})
        .finally(() => setLoadingSaved(false));
    }
  }, [open, itemId]);

  const fetchFromAI = async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    setFromSaved(false);
    try {
      const res = await axios.post(`${API}/inventory/ai-description`, {
        item_name: itemName,
        item_id: itemId,
      });
      if (res.data.medicine_info) {
        setInfo(res.data.medicine_info);
      } else if (res.data.raw_text) {
        setError('Could not parse AI response. Raw: ' + res.data.raw_text.substring(0, 300));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch medicine info');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!info || !itemId) return;
    setSaving(true);
    try {
      const parts = LANGS.map(lang => {
        const data = info[lang.key];
        if (!data) return '';
        return [
          `[${lang.label}]`,
          data.generic_name && `${FIELDS[0].label[lang.key]}: ${data.generic_name}`,
          data.description && `${FIELDS[2].label[lang.key]}: ${data.description}`,
          data.indications && `${FIELDS[3].label[lang.key]}: ${data.indications}`,
          data.dosage && `${FIELDS[4].label[lang.key]}: ${data.dosage}`,
          data.side_effects && `${FIELDS[5].label[lang.key]}: ${data.side_effects}`,
          data.contraindications && `${FIELDS[6].label[lang.key]}: ${data.contraindications}`,
          data.storage && `${FIELDS[7].label[lang.key]}: ${data.storage}`,
        ].filter(Boolean).join('\n');
      }).filter(Boolean);
      const description = parts.join('\n\n');

      await axios.post(`${API}/inventory/${itemId}/save-description`, { description });
      toast.success('Description saved to inventory');
      setFromSaved(true);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const langData = info?.[activeLang] || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setInfo(null); setError(null); setFromSaved(false); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="ai-medicine-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Medicine Info — {itemName}
          </DialogTitle>
        </DialogHeader>

        {/* Loading saved description */}
        {loadingSaved && !info && !loading && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground text-sm">Checking saved info...</p>
          </div>
        )}

        {/* No saved data, no AI data yet — show fetch button */}
        {!info && !loading && !loadingSaved && !error && (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <Sparkles className="w-12 h-12 text-purple-400/50" />
            <p className="text-muted-foreground text-center">
              No saved info found. Fetch pharmaceutical details for<br />
              <strong className="text-foreground">{itemName}</strong>
            </p>
            <Button onClick={fetchFromAI} className="gap-2" data-testid="ai-fetch-btn">
              <Sparkles className="w-4 h-4" /> Fetch Info with AI
            </Button>
          </div>
        )}

        {/* Loading from AI */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            <p className="text-muted-foreground text-sm">Analyzing with Claude Opus 4.6...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="py-6 text-center">
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <Button variant="outline" onClick={fetchFromAI}>Retry</Button>
          </div>
        )}

        {/* Info display (from saved or from AI) */}
        {info && (
          <div className="space-y-4">
            {/* Source badge + Refresh button */}
            <div className="flex items-center justify-between">
              <span className={`text-xs px-2 py-0.5 rounded-full ${fromSaved ? 'bg-green-500/15 text-green-400' : 'bg-purple-500/15 text-purple-400'}`}>
                {fromSaved ? 'Loaded from saved data' : 'Fetched from AI'}
              </span>
              <Button variant="ghost" size="sm" className="gap-1.5 text-purple-400 hover:text-purple-300" onClick={fetchFromAI} disabled={loading} data-testid="ai-refresh-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Refresh with AI
              </Button>
            </div>

            {/* Language tabs */}
            <div className="flex gap-1 border-b border-border pb-1" data-testid="ai-lang-tabs">
              {LANGS.map(lang => (
                <button
                  key={lang.key}
                  onClick={() => setActiveLang(lang.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                    activeLang === lang.key
                      ? 'bg-primary/10 text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  data-testid={`ai-lang-${lang.key}`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Info fields */}
            <div className="space-y-3" dir={activeLang === 'ar' ? 'rtl' : 'ltr'} data-testid="ai-info-content">
              {FIELDS.map(field => {
                const value = langData[field.key];
                if (!value) return null;
                return (
                  <div key={field.key} className="border border-border/50 rounded-lg p-3">
                    <div className="text-xs font-semibold text-primary/70 uppercase tracking-wide mb-1">
                      {field.label[activeLang] || field.label.en}
                    </div>
                    <div className="text-sm leading-relaxed">{value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {info && itemId && !fromSaved && (
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="ai-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save to Inventory
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIMedicineInfoDialog;
